import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { buildListScopeCrossLink, ListScope } from "../ListScopeFilter/listScope";

import type { CustomListsAutocompleteForSequencerItem } from "../../../List/components/CustomListsAutocompleteForSequencer";

interface MessagesEmptyColumnProps {
	/** The messaging surface the column belongs to — drives the copy and the cross-link. */
	scope: ListScope;
	/** The list filter currently applied, carried over to the counterpart surface. */
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	/**
	 * Whether the user is searching. PREC-2523: an empty column then means "nothing matched
	 * what you typed", which is a different statement from "this surface has nothing on it"
	 * and must not be dressed up as the latter. Filters are deliberately not counted — a
	 * search runs unscoped anyway, so the query is the only thing that can be to blame.
	 */
	hasSearchQuery: boolean;
}

/**
 * The empty state for the Outbox and Review-to-send leads columns (PREC-2523). Mirrors
 * `InboxEmptyScope`, which covers the Inbox on its own: outside a search it explains why the
 * surface is empty and — for the Inbox/Outbox pair — points at the counterpart where the leads
 * actually are; during a search it says only that nothing matched.
 */
export const MessagesEmptyColumn: FunctionComponent<MessagesEmptyColumnProps> = ({
	scope,
	selectedLists,
	hasSearchQuery,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "list.scopeFilter.irrelevant" });
	const { t: tMessages } = useTranslation("translation", { keyPrefix: "messages" });

	const crossLinkHref = buildListScopeCrossLink(scope, selectedLists);

	return (
		<div
			className="flex flex-col items-center gap-2 px-6 py-10 text-center"
			data-test="messages-empty-column"
		>
			{hasSearchQuery ? (
				<p className="text-sm font-medium text-gray-900">{tMessages("noResultsFound")}</p>
			) : (
				<>
					<p className="text-sm font-medium text-gray-900">{t(`${scope}.emptyTitle`)}</p>
					<p className="text-xs text-text-meta">{t(`${scope}.hint`)}</p>
					{crossLinkHref && (
						<a
							className="text-xs font-medium text-brand-light cursor-pointer hover:underline"
							data-test="messages-empty-column-cross-link"
							href={crossLinkHref}
						>
							{t(`${scope}.crossLink`)}
						</a>
					)}
				</>
			)}
		</div>
	);
};
